import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";

import { EventEditForm } from "@/components/ui/admin/events/EventEditForm";
import {
  createEventService,
  createPlaceService,
  createTaxonomyService,
  createTeamService,
} from "@/core/services/factory";
import { getAssignableTeamMembers } from "@/core/services/types/article.types";
import { getEventSharePath } from "@/core/services/types/event.types";
import { getTranslations } from "@/lib/translations";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";
import { useTranslations } from "@/lib/useTranslations";

export function EventEditPage() {
  const { id } = useParams<{ id: string }>();
  const t = useTranslations("events.edit");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["event-edit", id],
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const editT = getTranslations("events.edit");

      if (!id) throw new Error(editT("loadFailed"));

      const events = createEventService();
      const team = createTeamService();
      const places = createPlaceService();
      const taxonomy = createTaxonomyService();
      const event = await events.getForEdit(id);

      if (!event) {
        console.error("[EventEditPage] event not found", { id });
        throw new Error(editT("notFound"));
      }

      const [teamMembers, selectedPlace, [eventTaxonomy, taxonomyData]] =
        await Promise.all([
          team.listAll(),
          event.place_id
            ? places.getSelectOptionById(event.place_id)
            : Promise.resolve(null),
          Promise.all([events.getEventTaxonomy(id), taxonomy.listAll()]),
        ]);

      return { event, teamMembers, selectedPlace, eventTaxonomy, taxonomyData };
    },
  });

  if (!id) return <Navigate to={getLibraryReturnHref("events")} replace />;

  if (isLoading && !data) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-6 py-10 text-sm text-neutral-500">
        {t("loading")}
      </div>
    );
  }

  if (isError || !data?.event) {
    const message =
      error instanceof Error ? error.message : t("loadFailed");

    console.error("[EventEditPage] failed to load event", { id, error });

    return (
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-6 py-10">
        <p
          role="alert"
          className="wireframe-border rounded-xl px-4 py-3 text-sm text-whart-urgent"
        >
          {message}
        </p>
        <Link
          to={getLibraryReturnHref("events")}
          className="text-sm font-medium text-zinc-900 underline hover:text-neutral-600"
        >
          {t("backToLibrary")}
        </Link>
      </div>
    );
  }

  const { event, teamMembers, selectedPlace, eventTaxonomy, taxonomyData } = data;
  const assignableMembers = getAssignableTeamMembers(
    teamMembers,
    event.assigned_to,
  );
  const adminOrigin = import.meta.env.VITE_ADMIN_URL?.replace(/\/$/, "");
  const path = getEventSharePath(id);
  const shareUrl = adminOrigin ? `${adminOrigin}${path}` : path;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 overflow-y-auto px-6 py-10">
      <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
        {t("eyebrow")}
      </p>

      <EventEditForm
        key={event.id}
        event={event}
        assignableMembers={assignableMembers}
        selectedPlace={selectedPlace}
        taxonomy={eventTaxonomy}
        artForms={taxonomyData.artForms}
        artisticPeriods={taxonomyData.artisticPeriods}
        culturalAreas={taxonomyData.culturalAreas}
        exhibitionTypes={taxonomyData.exhibitionTypes}
        shareUrl={shareUrl}
      />
    </div>
  );
}
